import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { NextActionTableIcon } from '../components/NextActionMark'
import { OccupancyLease } from './types'
import {
    getLeaseType,
    placementSourceLabel,
    resolveOccupancyNextAction,
    unitIsChanged,
    unitPlacementCaption,
} from './occupancyLease'
import {
    LIFESTYLE_COLUMNS,
    fieldConflictsInUnit,
    lifestyleFieldValue,
    lifestyleImportance,
    petCellConflicts,
    genderCellConflicts,
    genderCellLabel,
    petCellLabel,
} from './occupancyLifestyle'
import { OccupancyMatchScore } from './OccupancyMatchScore'
import { personMatchScore, UnitMatchScore } from './occupancyMatchScoreUtils'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1, 1.5, 1.5),
        backgroundColor: '#fafafa',
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    header: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    tableWrap: {
        overflowX: 'auto',
        backgroundColor: '#fff',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        '& th, & td': {
            padding: '6px 8px',
            fontSize: '0.75rem',
            textAlign: 'left',
            borderBottom: `1px solid ${theme.palette.divider}`,
            verticalAlign: 'top',
        },
        '& th': {
            fontSize: '0.625rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.grey[50],
        },
        '& tr:last-child td': {
            borderBottom: 'none',
        },
    },
    name: {
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    scoreCell: {
        width: 72,
        whiteSpace: 'nowrap',
    },
    must: {
        backgroundColor: '#fff7ed',
    },
    conflict: {
        backgroundColor: '#fef2f2',
        boxShadow: 'inset 3px 0 0 #dc2626',
    },
    incomplete: {
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },
    mustMark: {
        display: 'block',
        fontSize: '0.5625rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: '#c2410c',
        marginBottom: 1,
    },
    changedChip: {
        backgroundColor: '#ffedd5',
        color: '#c2410c',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 20,
    },
    source: {
        display: 'block',
        fontWeight: 400,
        fontSize: '0.625rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
}))

function unitSummary(
    occupants: Array<OccupancyLease | null>,
    bedCount: number,
) {
    const people = occupants.filter((row): row is OccupancyLease => row != null)
    const renewals = people.filter((row) => getLeaseType(row) === 'renewal')
        .length
    const transfers = people.filter((row) => getLeaseType(row) === 'transfer')
        .length
    const suggested = people.filter(
        (row) => row.occupancy_assignment === 'suggested',
    ).length
    const open = bedCount - people.length
    const parts: string[] = []
    if (renewals) {
        parts.push(`${renewals} renewal${renewals === 1 ? '' : 's'} staying`)
    }
    if (transfers) {
        parts.push(`${transfers} transfer${transfers === 1 ? '' : 's'}`)
    }
    if (suggested) {
        parts.push(`${suggested} suggested`)
    }
    if (open) {
        parts.push(`${open} open bed${open === 1 ? '' : 's'}`)
    }
    if (!parts.length) {
        return 'This unit is full and confirmed.'
    }
    return parts.join(' · ')
}

export function OccupancyUnitReview({
    occupants,
    bedCount,
    match,
    leases,
    confirming,
    onConfirm,
    children,
}: {
    occupants: Array<OccupancyLease | null>
    bedCount: number
    match?: UnitMatchScore
    leases: OccupancyLease[]
    confirming?: boolean
    onConfirm: (leaseIds: number[]) => void
    children: React.ReactNode
}) {
    const classes = useStyles()
    const [confirmOpen, setConfirmOpen] = useState(false)
    const people = occupants.filter((row): row is OccupancyLease => row != null)
    const personScores = useMemo(() => {
        const map = new Map<number, UnitMatchScore>()
        for (const row of occupants) {
            if (!row) continue
            map.set(row.id, personMatchScore(row, occupants, leases))
        }
        return map
    }, [occupants, leases])
    const suggestedIds = people
        .filter((row) => row.occupancy_assignment === 'suggested')
        .map((row) => row.id)
    const changed = unitIsChanged(occupants, bedCount)
    const sourceCaption = unitPlacementCaption(occupants)

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box>
                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        style={{ gap: 8 }}
                    >
                        {match && <OccupancyMatchScore result={match} />}
                        <Typography variant="body2" color="textSecondary">
                            {unitSummary(occupants, bedCount)}
                        </Typography>
                        {changed && (
                            <Chip
                                label="Changed"
                                size="small"
                                className={classes.changedChip}
                            />
                        )}
                    </Box>
                    {sourceCaption ? (
                        <Typography variant="caption" color="textSecondary">
                            {sourceCaption}
                        </Typography>
                    ) : null}
                </Box>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={confirming || suggestedIds.length === 0}
                    onClick={() => setConfirmOpen(true)}
                    style={{ textTransform: 'none', fontWeight: 600 }}
                >
                    {confirming ? 'Confirming…' : 'Confirm Unit'}
                </Button>
            </Box>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Unit</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to confirm this unit?
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginTop: 8 }}
                    >
                        Confirming the unit will send out unit assignment
                        letters to the residents.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        disabled={confirming}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={confirming}
                        onClick={() => {
                            setConfirmOpen(false)
                            onConfirm(suggestedIds)
                        }}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {people.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                    Vacant unit — drag someone onto a bed to start this group.
                </Typography>
            ) : (
                <Box className={classes.tableWrap}>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th>Resident</th>
                                <th>Score</th>
                                {LIFESTYLE_COLUMNS.map((column) => (
                                    <th key={column.id}>{column.label}</th>
                                ))}
                                <th>Gender</th>
                                <th>Pets</th>
                            </tr>
                        </thead>
                        <tbody>
                            {people.map((lease) => {
                                const incomplete = !lease.lifestyle_complete
                                const source = placementSourceLabel(lease)
                                return (
                                    <tr key={lease.id}>
                                        <td className={classes.name}>
                                            <span className={classes.nameCell}>
                                                <NextActionTableIcon
                                                    action={resolveOccupancyNextAction(
                                                        lease,
                                                    )}
                                                />
                                                <span>
                                                    {lease.tenant_name}
                                                    {source && (
                                                        <span
                                                            className={
                                                                classes.source
                                                            }
                                                        >
                                                            {source}
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                        </td>
                                        <td className={classes.scoreCell}>
                                            <OccupancyMatchScore
                                                compact
                                                result={
                                                    personScores.get(lease.id)!
                                                }
                                            />
                                        </td>
                                        {LIFESTYLE_COLUMNS.map((column) => {
                                            const must =
                                                !incomplete &&
                                                lifestyleImportance(
                                                    lease,
                                                    column.id,
                                                ) === 'must'
                                            const conflict = fieldConflictsInUnit(
                                                lease,
                                                occupants,
                                                column.id,
                                            )
                                            const value = lifestyleFieldValue(
                                                lease,
                                                column.id,
                                            )
                                            return (
                                                <td
                                                    key={column.id}
                                                    className={[
                                                        conflict
                                                            ? classes.conflict
                                                            : '',
                                                        !conflict && must
                                                            ? classes.must
                                                            : '',
                                                        incomplete
                                                            ? classes.incomplete
                                                            : '',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ')}
                                                >
                                                    {must && (
                                                        <span
                                                            className={
                                                                classes.mustMark
                                                            }
                                                        >
                                                            Must
                                                        </span>
                                                    )}
                                                    {value}
                                                </td>
                                            )
                                        })}
                                        <td
                                            className={[
                                                genderCellConflicts(
                                                    lease,
                                                    occupants,
                                                )
                                                    ? classes.conflict
                                                    : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        >
                                            {genderCellLabel(lease)}
                                        </td>
                                        <td
                                            className={[
                                                petCellConflicts(
                                                    lease,
                                                    occupants,
                                                )
                                                    ? classes.conflict
                                                    : '',
                                                incomplete
                                                    ? classes.incomplete
                                                    : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        >
                                            {petCellLabel(lease)}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </Box>
            )}

            {children}
        </Box>
    )
}
